import { useRef, useState } from "react";
import { Paperclip, Camera, Trash2, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

// Files on a task (documents, photos). Phones can snap a photo straight from the camera.
export const TaskAttachments = ({ taskId, initial = [], canDeleteAll, myEmail, onChange }) => {
    const [items, setItems] = useState(initial);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    const camRef = useRef(null);

    const update = (next) => { setItems(next); onChange?.(next); };

    const upload = async (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { toast.error("File is too large (max 10 MB)"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/tasks/${taskId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            update([...items, data]);
            toast.success("File attached");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    const open = async (att) => {
        try {
            const { data } = await api.get(`/tasks/${taskId}/attachments/${att.id}`, { responseType: "blob" });
            window.open(URL.createObjectURL(data), "_blank");
        } catch (err) {
            toast.error("Could not open file");
        }
    };

    const remove = async (att) => {
        try {
            await api.delete(`/tasks/${taskId}/attachments/${att.id}`);
            update(items.filter((a) => a.id !== att.id));
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not remove file");
        }
    };

    return (
        <div data-testid="task-attachments">
            <span className="text-[10px] uppercase tracking-[0.15em] text-ink/50">Files</span>
            <ul className="mt-1 divide-y divide-ink/10 border border-ink/10">
                {items.length === 0 && <li className="px-3 py-2 text-xs text-ink/40">No files yet</li>}
                {items.map((a) => (
                    <li key={a.id} className="px-3 py-2 flex items-center gap-2 text-sm" data-testid={`task-attachment-${a.id}`}>
                        <FileText className="w-4 h-4 text-ink/40 shrink-0" />
                        <button type="button" onClick={() => open(a)} className="truncate text-left text-klein hover:underline flex-1" data-testid={`task-attachment-open-${a.id}`}>
                            {a.filename}
                        </button>
                        <span className="text-[11px] text-ink/40 shrink-0">{Math.max(1, Math.round((a.size || 0) / 1024))} KB</span>
                        {(canDeleteAll || a.uploaded_by === myEmail) && (
                            <button type="button" onClick={() => remove(a)} className="p-1 text-ink/40 hover:text-red-600" data-testid={`task-attachment-delete-${a.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </li>
                ))}
            </ul>
            <div className="mt-2 flex gap-2">
                <input ref={fileRef} type="file" hidden onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="task-attachment-input" />
                <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="task-attachment-camera-input" />
                <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 border border-ink/15 px-3 py-2 text-xs hover:bg-ink hover:text-white" data-testid="task-attachment-upload-btn">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} Attach file
                </button>
                <button type="button" disabled={busy} onClick={() => camRef.current?.click()} className="inline-flex items-center gap-1.5 border border-ink/15 px-3 py-2 text-xs hover:bg-ink hover:text-white" data-testid="task-attachment-camera-btn">
                    <Camera className="w-3.5 h-3.5" /> Take photo
                </button>
            </div>
        </div>
    );
};
